import React from "react";
import { Link } from "react-router-dom";
import { LockKeyhole, ShieldX, ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { hasAnyRole } from "@/lib/roles";

// Page gate: shows the page only to the right people.
// - not logged in  -> "Log in" screen (returns to this page after login)
// - wrong role     -> "No access" screen
// This only hides screens. The database access rules (base44/entities/*.jsonc) are what protect the data.
// role="admin": Base44 account role "admin" (organisers and platform admins, see docs/security-access-rules.md).
export default function RequireAccess({ role, roles, area = "this area", children }) {
  const { user, isAuthenticated, isLoadingAuth, navigateToLogin } = useAuth();

  if (isLoadingAuth) {
    return (
      <div className="tf-public flex min-h-screen items-center justify-center" aria-busy="true" aria-label="Checking your login">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-tf-blue/20 border-t-tf-blue motion-reduce:animate-none" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Gate icon={LockKeyhole} title="Please log in" text={`You need to log in to open ${area}.`}>
        <button onClick={navigateToLogin} className="inline-flex h-11 items-center justify-center rounded-tf-control bg-tf-blue px-6 text-sm font-semibold text-white hover:bg-tf-blue-deep">Log in</button>
      </Gate>
    );
  }

  if (role === "admin" && user?.role !== "admin") {
    return (
      <Gate icon={ShieldX} title="You don't have access" text={`Your account can't open ${area}. If you think this is wrong, contact the tournament organiser.`} />
    );
  }

  if (roles?.length && !hasAnyRole(user, roles)) {
    return (
      <Gate icon={ShieldX} title="You don't have access" text={`Your account can't open ${area}. If you think this is wrong, contact the tournament organiser.`} />
    );
  }

  return children;
}

function Gate({ icon: Icon, title, text, children }) {
  return (
    <div className="tf-public flex min-h-screen flex-col items-center justify-center gap-2 p-6 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-tf-blue/10 text-tf-blue" aria-hidden><Icon className="h-7 w-7" /></span>
      <h1 className="mt-2 text-xl font-bold">{title}</h1>
      <p className="max-w-sm text-sm text-tf-muted">{text}</p>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
        {children}
        <Link to="/" className="inline-flex h-11 items-center gap-1.5 rounded-tf-control px-4 text-sm font-semibold text-tf-blue hover:bg-tf-blue/10"><ArrowLeft className="h-4 w-4" aria-hidden />Home</Link>
      </div>
    </div>
  );
}
